import { useEffect, useMemo, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Check, Loader2, Plus, Search, X } from 'lucide-react'
import type { ProcessStepConnection } from '@/api/processStepsApi'
import type { ApiEquipmentType } from '@/api/equipmentApi'
import { useEquipmentTypes } from '@/hooks/useEquipment'
import { useUpsertProcessStep } from '@/hooks/useProcessSteps'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { FeatureDisabledNote } from '@/components/auth/FeatureDisabledNote'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

// ─── Local editable row ──────────────────────────────────────────────────────
//
// One equipment requirement being edited. Seeded from the loaded connection and
// diffed against it to drive the dirty/Save state. Numeric fields are held as
// strings so partial input (e.g. "0.") doesn't fight the controlled input.

interface DraftRow {
  equipmentTypeId: string
  /** Legacy unit binding — preserved on load/save but no longer authored here. */
  equipmentId: string | null
  prepHrs: string
  setupHrs: string
  runHrs: string
  unloadHrs: string
  runsPerDay: string
}

function toDraft(r: {
  equipmentTypeId: string
  equipmentId: string | null
  prepHrs?: number
  setupHrs: number
  runHrs: number
  unloadHrs: number
  runsPerDay: number
}): DraftRow {
  return {
    equipmentTypeId: r.equipmentTypeId,
    equipmentId: r.equipmentId,
    // Optional on the way in: a connection saved before PREP existed carries no
    // value, and String(undefined) would put "undefined" in the input.
    prepHrs: String(r.prepHrs ?? 0),
    setupHrs: String(r.setupHrs),
    runHrs: String(r.runHrs),
    unloadHrs: String(r.unloadHrs),
    runsPerDay: String(r.runsPerDay),
  }
}

/** Parse a fractional-hours input; NaN / negative collapse to 0. */
function hrs(value: string): number {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

/** Parse a runs-per-day input; integer ≥ 1, NaN collapses to 1. */
function runs(value: string): number {
  const n = Math.floor(Number(value))
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * A requirement that books no time cannot be reserved, and the backend refuses
 * it. Checked here too so the answer arrives while the author is still looking
 * at the row, rather than as a round trip that names a type but not a field.
 *
 * **Prep is deliberately not counted.** It delays a hold rather than creating
 * one, so a row with 2 h prep and nothing else still books no equipment — and
 * the backend's matching check would refuse it on save.
 */
function hasNoRunTime(r: DraftRow): boolean {
  return !(hrs(r.setupHrs) + hrs(r.runHrs) + hrs(r.unloadHrs) > 0)
}

function serialize(rows: DraftRow[]): string {
  return JSON.stringify(
    rows.map((r) => ({
      equipmentTypeId: r.equipmentTypeId,
      equipmentId: r.equipmentId,
      prepHrs: hrs(r.prepHrs),
      setupHrs: hrs(r.setupHrs),
      runHrs: hrs(r.runHrs),
      unloadHrs: hrs(r.unloadHrs),
      runsPerDay: runs(r.runsPerDay),
    })),
  )
}

// A compact numeric cell input for the requirements table.
function CellInput({
  label,
  value,
  onChange,
  min = 0,
  step,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  min?: number
  step?: string
}) {
  return (
    <input
      type='number'
      inputMode='decimal'
      aria-label={label}
      min={min}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className='h-8 w-20 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
    />
  )
}

// ─── Equipment type picker dialog ────────────────────────────────────────────
//
// Browsable catalog of equipment TYPES (a requirement books capacity of a
// type, not a specific unit — units are assigned at execution/scheduling
// time). Grouped by registry group, searchable by type or group, with group
// filter chips; added types stay visible with a green check (click to
// remove), and the list scrolls so large registries stay manageable.

function EquipmentPickerDialog({
  open,
  onClose,
  types,
  loading,
  addedIds,
  onAdd,
  onRemove,
}: {
  open: boolean
  onClose: () => void
  types: ApiEquipmentType[]
  loading: boolean
  addedIds: Set<string>
  onAdd: (typeId: string) => void
  onRemove: (typeId: string) => void
}) {
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<string>('all')

  // Reset filters each time the picker opens.
  useEffect(() => {
    if (open) {
      setQuery('')
      setGroup('all')
    }
  }, [open])

  const groups = useMemo(
    () => [...new Set(types.map((t) => t.group?.name).filter(Boolean))].sort() as string[],
    [types],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return types.filter((t) => {
      if (group !== 'all' && t.group?.name !== group) return false
      if (!q) return true
      return (
        t.name.toLowerCase().includes(q) ||
        (t.group?.name ?? '').toLowerCase().includes(q)
      )
    })
  }, [types, query, group])

  // Group the filtered types under their registry group for section headers.
  const sections = useMemo(() => {
    const byGroup = new Map<string, ApiEquipmentType[]>()
    for (const t of filtered) {
      const g = t.group?.name ?? 'Ungrouped'
      const list = byGroup.get(g) ?? []
      list.push(t)
      byGroup.set(g, list)
    }
    return [...byGroup.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, list]) => ({
        name,
        types: list.sort((a, b) => a.name.localeCompare(b.name)),
      }))
  }, [filtered])

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl'>
        <DialogHeader className='border-b border-gray-100 px-6 pb-4 pt-5'>
          <DialogTitle>Add equipment</DialogTitle>
          <DialogDescription>
            Search by equipment type or group — click <Plus className='inline h-3 w-3' /> to add
            a type to this process step.
          </DialogDescription>
        </DialogHeader>

        {/* Search + group filter */}
        <div className='space-y-2.5 border-b border-gray-100 px-6 py-3'>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search equipment type or group…'
              className='h-9 pl-8'
            />
          </div>
          {groups.length > 1 && (
            <div className='flex flex-wrap gap-1.5'>
              {['all', ...groups].map((g) => (
                <button
                  key={g}
                  type='button'
                  onClick={() => setGroup(g)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    group === g
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-200 bg-white text-muted-foreground hover:bg-gray-50',
                  )}
                >
                  {g === 'all' ? 'All groups' : g}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Type list */}
        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-3'>
          {loading && (
            <p className='py-8 text-center text-sm text-muted-foreground'>
              <Loader2 className='mr-2 inline h-4 w-4 animate-spin' />
              Loading equipment…
            </p>
          )}
          {!loading && sections.length === 0 && (
            <p className='py-8 text-center text-sm text-muted-foreground'>
              No equipment matches “{query}”.
            </p>
          )}
          {sections.map((section) => (
            <div key={section.name} className='mb-3'>
              <p className='sticky top-0 z-10 -mx-1 bg-background px-1 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                {section.name}
                <span className='ml-1.5 font-normal normal-case text-gray-300'>
                  ({section.types.length})
                </span>
              </p>
              <ul className='space-y-1'>
                {section.types.map((t) => {
                  const added = addedIds.has(t.id)
                  return (
                    <li
                      key={t.id}
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors',
                        added ? 'border-green-200 bg-green-50/60' : 'border-gray-200 bg-white',
                      )}
                    >
                      <div className='min-w-0'>
                        <p className='truncate text-sm font-medium text-foreground'>{t.name}</p>
                        <p className='truncate text-xs text-muted-foreground'>
                          {t.group?.name ?? '—'}
                          {typeof t.unitCount === 'number' ? ` · ${t.unitCount} unit${t.unitCount === 1 ? '' : 's'}` : ''}
                        </p>
                      </div>
                      {added ? (
                        <button
                          type='button'
                          onClick={() => onRemove(t.id)}
                          title='Added — click to remove'
                          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600 text-white transition-colors hover:bg-gray-400'
                        >
                          <Check className='h-4 w-4' strokeWidth={3} />
                        </button>
                      ) : (
                        <button
                          type='button'
                          onClick={() => onAdd(t.id)}
                          title='Add to process step'
                          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors hover:border-primary hover:bg-primary hover:text-white'
                        >
                          <Plus className='h-4 w-4' />
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        <DialogFooter className='flex-row items-center border-t border-gray-100 px-6 py-3 sm:justify-between'>
          <p className='text-xs text-muted-foreground'>
            {addedIds.size} of {types.length} added
          </p>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Tab ─────────────────────────────────────────────────────────────────────

// Per-user column layout. Equipment type is locked (it identifies the line) and
// so is the remove control. See src/components/table/index.ts.
const EQUIPMENT_REQ_TABLE_ID = 'process-equipment-required'

const EQUIPMENT_REQ_COLUMNS: TableColumnDef[] = [
  { id: 'equipmentType', header: 'Equipment type', locked: true },
  { id: 'group', header: 'Group' },
  { id: 'prep', header: 'Prep (h)' },
  { id: 'setup', header: 'Setup (h)' },
  { id: 'run', header: 'Run (h)' },
  { id: 'unload', header: 'Unload (h)' },
  { id: 'runsPerDay', header: 'Runs / day' },
  { id: 'actions', header: 'Remove', locked: true },
]

const EQUIPMENT_REQ_HEAD_CLASSES: Record<string, string | undefined> = {
  prep: 'text-right',
  setup: 'text-right',
  run: 'text-right',
  unload: 'text-right',
  runsPerDay: 'text-right',
  actions: 'w-12',
}

// Prep explains itself on hover: it is the one column whose meaning is not
// obvious from the label.
const EQUIPMENT_REQ_HEAD_TITLES: Record<string, string | undefined> = {
  prep: 'Delays the reservation — the unit is not held while its bench is being prepped',
}

export function EquipmentRequirementTab({
  fieldId,
  connection,
}: {
  fieldId: string
  connection: ProcessStepConnection | null
}) {
  const cols = useTableColumns(EQUIPMENT_REQ_TABLE_ID, EQUIPMENT_REQ_COLUMNS)
  const equipmentOn = useFeatureFlag(FEATURES.equipment)
  const typesQuery = useEquipmentTypes()
  const types = useMemo(() => typesQuery.data ?? [], [typesQuery.data])
  const typeById = useMemo(
    () => new Map(types.map((t) => [t.id, t])),
    [types],
  )

  const upsert = useUpsertProcessStep()

  // Seed local state from the connection; re-seed when the connection (or field) changes.
  const [rows, setRows] = useState<DraftRow[]>([])
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const baseline = useMemo(
    () => (connection?.equipmentRequirements ?? []).map(toDraft),
    [connection],
  )

  useEffect(() => {
    setRows(baseline)
    setSavedAt(null)
  }, [baseline, fieldId])

  const dirty = serialize(rows) !== serialize(baseline)

  const invalidRows = useMemo(
    () => new Set(rows.filter(hasNoRunTime).map((r) => r.equipmentTypeId)),
    [rows],
  )
  const invalidNames = useMemo(
    () => [...invalidRows].map((id) => typeById.get(id)?.name ?? id),
    [invalidRows, typeById],
  )

  // Type ids already added as requirement rows — drives the picker's added state.
  const addedTypeIds = useMemo(
    () => new Set(rows.map((r) => r.equipmentTypeId)),
    [rows],
  )

  const addType = (typeId: string) => {
    if (!typeById.has(typeId) || rows.some((r) => r.equipmentTypeId === typeId)) return
    setRows((prev) => [
      ...prev,
      {
        equipmentTypeId: typeId,
        equipmentId: null,
        prepHrs: '0',
        setupHrs: '0',
        runHrs: '0',
        unloadHrs: '0',
        runsPerDay: '1',
      },
    ])
    setSavedAt(null)
  }

  const patchRow = (index: number, patch: Partial<DraftRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
    setSavedAt(null)
  }

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index))
    setSavedAt(null)
  }

  const removeType = (typeId: string) => {
    setRows((prev) => prev.filter((r) => r.equipmentTypeId !== typeId))
    setSavedAt(null)
  }

  const save = () => {
    // Equipment only — inventory and staffing are omitted, and the endpoint
    // keeps what it is not sent.
    upsert.mutate(
      {
        fieldId,
        payload: {
          equipmentRequirements: rows.map((r, i) => ({
            equipmentTypeId: r.equipmentTypeId,
            equipmentId: r.equipmentId ?? undefined,
            prepHrs: hrs(r.prepHrs),
            setupHrs: hrs(r.setupHrs),
            runHrs: hrs(r.runHrs),
            unloadHrs: hrs(r.unloadHrs),
            runsPerDay: runs(r.runsPerDay),
            order: i,
          })),
        },
      },
      { onSuccess: () => setSavedAt(Date.now()) },
    )
  }

  // The whole tab is an equipment picker: with the feature off, useEquipmentTypes
  // 403s to [], so "Add equipment" opens an empty catalog and saved rows show raw
  // ids. Refuse the authoring surface outright rather than let someone build
  // requirements against a module their tenant cannot use.
  if (!equipmentOn) {
    return (
      <div className='px-5 py-5'>
        <FeatureDisabledNote label='Equipment' />
      </div>
    )
  }

  return (
    <div className='space-y-4 px-5 py-5'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <Button variant='outline' onClick={() => setPickerOpen(true)}>
            <Plus className='mr-1.5 h-4 w-4' />
            Add equipment
          </Button>
          {rows.length > 0 && (
            <span className='text-xs text-muted-foreground'>{rows.length} added</span>
          )}
        </div>
        <div className='flex items-center gap-3'>
          {savedAt && !dirty && (
            <span className='flex items-center gap-1 text-xs text-green-600'>
              <Check className='h-3.5 w-3.5' /> Saved
            </span>
          )}
          <Button onClick={save} disabled={!dirty || upsert.isPending || invalidNames.length > 0}>
            {upsert.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Save
          </Button>
        </div>
      </div>

      {invalidNames.length > 0 && (
        <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
          {invalidNames.join(', ')} {invalidNames.length === 1 ? 'has' : 'have'} no run time. Set
          setup, run or unload hours — equipment with no run time cannot be reserved.
        </p>
      )}

      {upsert.isError && (
        <p className='text-sm text-destructive'>
          {upsert.error instanceof Error ? upsert.error.message : 'Could not save equipment.'}
        </p>
      )}

      {/* Added requirements as an editable table */}
      {rows.length === 0 ? (
        <p className='rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-muted-foreground'>
          No equipment added yet — click “Add equipment” to pick equipment types.
        </p>
      ) : (
        <>
        <div className='overflow-x-auto rounded-xl border border-gray-200'>
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    title={EQUIPMENT_REQ_HEAD_TITLES[def.id]}
                    className={EQUIPMENT_REQ_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => {
                const type = typeById.get(row.equipmentTypeId)
                const name = type?.name ?? 'Unknown equipment'
                return (
                  <TableRow
                    key={row.equipmentTypeId}
                    className={cn(hasNoRunTime(row) && 'bg-amber-50/70')}
                  >
                    <ColumnCells
                      columns={cols}
                      cells={{
                        equipmentType: <TableCell className='font-medium'>{name}</TableCell>,
                        group: (
                          <TableCell className='text-muted-foreground'>
                            {type?.group?.name ?? '—'}
                          </TableCell>
                        ),
                        prep: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Prep hours for ${name}`}
                              step='0.25'
                              value={row.prepHrs}
                              onChange={(v) => patchRow(i, { prepHrs: v })}
                            />
                          </TableCell>
                        ),
                        setup: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Setup hours for ${name}`}
                              step='0.25'
                              value={row.setupHrs}
                              onChange={(v) => patchRow(i, { setupHrs: v })}
                            />
                          </TableCell>
                        ),
                        run: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Run hours for ${name}`}
                              step='0.25'
                              value={row.runHrs}
                              onChange={(v) => patchRow(i, { runHrs: v })}
                            />
                          </TableCell>
                        ),
                        unload: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Unload hours for ${name}`}
                              step='0.25'
                              value={row.unloadHrs}
                              onChange={(v) => patchRow(i, { unloadHrs: v })}
                            />
                          </TableCell>
                        ),
                        runsPerDay: (
                          <TableCell className='text-right'>
                            <CellInput
                              label={`Runs per day for ${name}`}
                              min={1}
                              step='1'
                              value={row.runsPerDay}
                              onChange={(v) => patchRow(i, { runsPerDay: v })}
                            />
                          </TableCell>
                        ),
                        actions: (
                          <TableCell>
                            <button
                              type='button'
                              aria-label='Remove equipment'
                              title='Remove equipment'
                              onClick={() => removeRow(i)}
                              className='flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-600 hover:text-white'
                            >
                              <X className='h-4 w-4' strokeWidth={2.5} />
                            </button>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
        </>
      )}

      <EquipmentPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        types={types}
        loading={typesQuery.isLoading}
        addedIds={addedTypeIds}
        onAdd={addType}
        onRemove={removeType}
      />
    </div>
  )
}
